import { messengerClassLabel, messengerBLabel } from "@/lib/messengerStoryLabels";
import { formatNumber } from "@/lib/utils";

type StatsPayload = {
  threads?: number;
  messages_user?: number;
  messages_page?: number;
  stories?: number;
  voice?: number;
  ops_bank?: number;
  user_classes?: Record<string, number>;
  b_signals?: { id: string; count: number }[];
  generated_at?: string;
  indexed_at?: string | null;
  has_corpus_stats?: boolean;
};

type Props = {
  stats?: StatsPayload | null;
};

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" title={hint}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

export default function MessengerStatsStrip({ stats }: Props) {
  if (!stats) return null;

  const classEntries = Object.entries(stats.user_classes || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const classTotal = classEntries.reduce((s, [, n]) => s + n, 0) || 1;
  const freshness = stats.generated_at
    ? `Indeks: ${String(stats.generated_at).slice(0, 10)}`
    : stats.has_corpus_stats
      ? "Korpus bez daty"
      : "Brak snapshotu korpusu (przebuduj indeks)";
  const semantic = stats.indexed_at ? ` · semantic ${String(stats.indexed_at).slice(0, 10)}` : "";

  return (
    <section className="mb-6 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label="Wątki" value={formatNumber(stats.threads ?? 0)} hint="Rozmowy w surowym inboxie" />
        <Kpi
          label="Wiad. user"
          value={formatNumber(stats.messages_user ?? 0)}
          hint="Wszystkie wiadomości użytkowników (nie tylko bank)"
        />
        <Kpi label="Historie" value={formatNumber(stats.stories ?? 0)} hint="Tier story w banku panelu" />
        <Kpi label="Głos / opinie" value={formatNumber(stats.voice ?? 0)} hint="Tier voice: pomiędzy historią a FAQ" />
        <Kpi
          label="W banku"
          value={formatNumber(stats.ops_bank ?? 0)}
          hint="Pozycje przeszukiwalne (story + voice)"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-900">Klasy w całym korpusie user</h2>
          <span className="text-xs text-slate-500">
            {freshness}
            {semantic}
          </span>
        </div>
        {classEntries.length === 0 ? (
          <p className="text-sm text-slate-500">
            Brak breakdownu klas. Uruchom rebuild ops index, żeby zapisać meta.stats.
          </p>
        ) : (
          <ul className="space-y-2">
            {classEntries.map(([key, count]) => (
              <li key={key} className="flex items-center gap-3 text-sm">
                <span className="w-36 shrink-0 truncate text-slate-700" title={messengerClassLabel(key)}>
                  {messengerClassLabel(key)}
                </span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-brand/70"
                    style={{ width: `${Math.max(4, Math.round((count / classTotal) * 100))}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right tabular-nums text-slate-500">{count}</span>
              </li>
            ))}
          </ul>
        )}
        {(stats.b_signals || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {(stats.b_signals || []).slice(0, 6).map((b) => (
              <span
                key={b.id}
                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
              >
                {messengerBLabel(b.id)} · {b.count}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
